import { rootURL } from "./rootURL";

// The access token lives in memory only — it expires in 15 minutes, and keeping
// it out of storage means a reload cannot resurrect one. The refresh token does
// go to localStorage, otherwise every reload would demand the password again.
//
// An httpOnly cookie would put the refresh token out of reach of XSS, but it
// would also have to be a cross-site cookie once the API moves to its own
// origin, with the SameSite=None and Safari handling that implies. For an
// internal tool behind a login, the bearer-token shape the app already used for
// Firebase ID tokens is the simpler trade.
const REFRESH_TOKEN_KEY = "sunrise.refreshToken";

let accessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;

const signOutListeners = new Set<() => void>();

export function getAccessToken(): string | null {
	return accessToken;
}

export function getRefreshToken(): string | null {
	try {
		return localStorage.getItem(REFRESH_TOKEN_KEY);
	} catch {
		// Safari in private mode throws rather than returning null.
		return null;
	}
}

export function setTokens(tokens: {
	accessToken: string;
	refreshToken: string;
}): void {
	accessToken = tokens.accessToken;
	try {
		localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
	} catch {
		// Storage unavailable: the session still works until the tab closes.
	}
}

export function clearTokens(): void {
	accessToken = null;
	try {
		localStorage.removeItem(REFRESH_TOKEN_KEY);
	} catch {
		// Nothing to do — the in-memory token is gone either way.
	}
}

export function onSignedOut(listener: () => void): () => void {
	signOutListeners.add(listener);
	return () => signOutListeners.delete(listener);
}

function notifySignedOut(): void {
	for (const listener of signOutListeners) {
		listener();
	}
}

/**
 * Exchanges the stored refresh token for a fresh pair.
 *
 * Concurrent callers share one request. Without that, a page that fires six
 * queries at once would send six refreshes, and since the server rotates on
 * every use, five of them would arrive with a token that had just been
 * superseded — which the server reads as theft and answers by revoking every
 * session for the account.
 */
export function refreshTokens(): Promise<boolean> {
	if (refreshInFlight) {
		return refreshInFlight;
	}

	const refreshToken = getRefreshToken();
	if (!refreshToken) {
		return Promise.resolve(false);
	}

	refreshInFlight = fetch(`${rootURL}auth/refresh`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ refreshToken }),
	})
		.then(async (response) => {
			if (!response.ok) {
				clearTokens();
				notifySignedOut();
				return false;
			}
			const body = await response.json();
			setTokens({
				accessToken: body.accessToken,
				refreshToken: body.refreshToken,
			});
			return true;
		})
		.catch(() => {
			// A network blip is not a sign-out: keep the refresh token so the next
			// attempt can still use it, and just report the failure.
			return false;
		})
		.finally(() => {
			refreshInFlight = null;
		});

	return refreshInFlight;
}

export async function signOut(): Promise<void> {
	const refreshToken = getRefreshToken();
	clearTokens();

	if (refreshToken) {
		try {
			await fetch(`${rootURL}auth/logout`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ refreshToken }),
			});
		} catch {
			// Local tokens are already gone; a failed revoke only means the session
			// row lives until it expires on its own.
		}
	}

	notifySignedOut();
}
